import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TokenHandlerService } from '@celerofinancas/ui-oauth';
import { TokenExchangeEvents } from '@shared/models/token-exchange-events';
import { Observable, filter, switchMap, take } from 'rxjs';

/**
 * URLs that must never wait for a session — they are how a session is obtained.
 *
 * Holding any of these would deadlock: the interceptor would wait for a token that
 * only the request it is holding could produce.
 */
const AUTH_WHITE_LIST = [
  'auth/access_token',
  'auth/token_exchange',
  'auth/host_token',
  'auth/refresh_token',
  'auth/logout',
  'openid-configuration',
  'csrf-token',
];

/**
 * Holds requests until the token exchange resolves.
 *
 * ## The race it closes
 *
 * The screens call the API from `ngOnInit` (`LoadUserAction`,
 * `LoadActiveCoaAction`, the lists), and `ngOnInit` runs **before** the portal
 * writes the `token` attribute — Angular Elements sets inputs after the element is
 * connected. So the first requests would go out with no `Authorization`, take a
 * `403` and paint the screen with zeroes and an error in the console, while the
 * session arrives milliseconds later and nothing retries.
 *
 * Same interceptor `serasa-cl-webcomponents` runs for the same reason.
 *
 * ## Why it also listens for the failure
 *
 * Waiting only for `TOKEN_RECEIVED` would hang the request forever when the
 * exchange fails. On `TOKEN_EXCHANGED_FAILED` it throws instead, so the caller
 * sees an error and `FailureWrapperComponent` can render the error screen.
 */
@Injectable({ providedIn: 'root' })
export class TokenRaceConditionInterceptor implements HttpInterceptor {
  /**
   * Token storage and session events.
   */
  private readonly tokens = inject(TokenHandlerService);

  /**
   * Lets the request through, or parks it until the session resolves.
   *
   * @param request outgoing request
   * @param next next handler in the chain
   * @returns the response stream, possibly delayed until the token arrives
   */
  public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (AUTH_WHITE_LIST.some((url) => request.url.includes(url))) {
      return next.handle(request);
    }

    if (this.tokens.accessToken) {
      return next.handle(request);
    }

    return this.tokens.eventsSubject$.pipe(
      filter(
        (event) =>
          event['event'] === TokenExchangeEvents.TOKEN_RECEIVED ||
          event['event'] === TokenExchangeEvents.TOKEN_EXCHANGED_FAILED,
      ),
      take(1),
      switchMap((event) => {
        if (event['event'] === TokenExchangeEvents.TOKEN_EXCHANGED_FAILED) {
          throw new Error('Token exchange failed');
        }

        return this.intercept(request, next);
      }),
    );
  }
}
